import logger from '../utils/logger.js';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Badge,
  Pagination,
  Stack,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList,
  Message,
  Feedback,
  Replay as ReplayIcon,
  GridOn as GridOnIcon,
  ContentCut as SurgeryIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getSessionFetchErrorMessage } from '../utils/auth';
import { formatSessionDateTime } from '../utils/sessionDateTime';
import { SESSION_MODE_OPTIONS } from '../utils/sessionMode';
import SessionModeChip from './SessionModeChip';
import SessionPatientIdLabel from './SessionPatientIdLabel';
import { getSessionPatientDisplayName } from '../utils/sessionHistoryPatientDisplay';
import { SUPPORTED_LANGUAGES } from '../config/locales';

const EMPTY_FILTERS = {
  session_id: '',
  persona_id: '',
  min_messages: '',
  max_messages: '',
  start_date: '',
  end_date: '',
  has_feedback: '',
  mode: '',
  languages: [],
};

const ClientSessionHistoryView = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoadError, setSessionsLoadError] = useState('');

  const getInitialFilters = useCallback(() => {
    const languagesParam = searchParams.get('languages');
    const languages = languagesParam ? languagesParam.split(',').filter(Boolean) : [];
    return {
      session_id: searchParams.get('session_id') || '',
      persona_id: searchParams.get('persona_id') || '',
      min_messages: searchParams.get('min_messages') || '',
      max_messages: searchParams.get('max_messages') || '',
      start_date: searchParams.get('start_date') || '',
      end_date: searchParams.get('end_date') || '',
      has_feedback: searchParams.get('has_feedback') || '',
      mode: searchParams.get('mode') || '',
      languages,
    };
  }, [searchParams]);

  const getInitialPage = useCallback(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  }, [searchParams]);

  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [totalSessions, setTotalSessions] = useState(0);
  const sessionsPerPage = 10;

  const [filterForm, setFilterForm] = useState(getInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const initialFetchDone = useRef(false);
  const lastSearchParamsKeyRef = useRef(null);

  const updateUrlParams = useCallback((newFilters, newPage) => {
    const params = new URLSearchParams();
    params.set('tab', 'sessions');

    Object.entries(newFilters).forEach(([key, value]) => {
      if (key === 'languages') {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(','));
        }
      } else if (value) {
        params.set(key, value);
      }
    });

    if (newPage > 1) params.set('page', newPage.toString());

    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchSessions = useCallback(async (page = 1, filters = appliedFilters) => {
    try {
      setLoading(true);
      setSessionsLoadError('');

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'languages') {
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((lang) => params.append('languages', lang));
          }
        } else if (value) {
          params.append(key, value);
        }
      });

      params.append('archived', 'false');
      params.append('page', String(page));
      params.append('pageSize', String(sessionsPerPage));

      const response = await axios.get(`/api/auth/me/sessions?${params.toString()}`);
      setTotalSessions(response.data.total ?? 0);
      setSessions(response.data.sessions || []);
      setCurrentPage(page);
    } catch (error) {
      logger.error('Error fetching sessions:', error);
      setSessions([]);
      setTotalSessions(0);
      setSessionsLoadError(
        getSessionFetchErrorMessage(error, 'Failed to fetch session data.')
      );
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, sessionsPerPage]);

  useEffect(() => {
    const loadPersonas = async () => {
      try {
        const response = await axios.get('/api/personas');
        const collected = [...(response.data || [])];
        collected.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        );
        setPersonas(collected);
      } catch (error) {
        logger.error('Error fetching personas:', error);
      }
    };
    loadPersonas();
  }, []);

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      const urlParams = new URLSearchParams(window.location.search);
      const initialPage = urlParams.get('page') ? parseInt(urlParams.get('page'), 10) : 1;
      fetchSessions(initialPage);
    }
  }, [fetchSessions]);

  useEffect(() => {
    const searchParamsKey = searchParams.toString();
    if (lastSearchParamsKeyRef.current === searchParamsKey) {
      return;
    }
    lastSearchParamsKeyRef.current = searchParamsKey;

    if (!initialFetchDone.current) return;

    const languagesParam = searchParams.get('languages');
    const languages = languagesParam ? languagesParam.split(',').filter(Boolean) : [];
    const urlFilters = {
      session_id: searchParams.get('session_id') || '',
      persona_id: searchParams.get('persona_id') || '',
      min_messages: searchParams.get('min_messages') || '',
      max_messages: searchParams.get('max_messages') || '',
      start_date: searchParams.get('start_date') || '',
      end_date: searchParams.get('end_date') || '',
      has_feedback: searchParams.get('has_feedback') || '',
      mode: searchParams.get('mode') || '',
      languages,
    };
    const urlPage = searchParams.get('page') ? parseInt(searchParams.get('page'), 10) : 1;

    let needsRefetch = false;

    const filtersChanged = Object.keys(urlFilters).some((key) => {
      if (key === 'languages') {
        const formLangs = urlFilters[key] || [];
        const appliedLangs = appliedFilters[key] || [];
        if (formLangs.length !== appliedLangs.length) return true;
        return formLangs.some((lang, idx) => lang !== appliedLangs[idx]);
      }
      return urlFilters[key] !== appliedFilters[key];
    });

    if (filtersChanged) {
      setFilterForm(urlFilters);
      setAppliedFilters(urlFilters);
      needsRefetch = true;
    }

    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
      needsRefetch = true;
    }

    if (needsRefetch) {
      fetchSessions(urlPage, urlFilters);
    }
  }, [searchParams, appliedFilters, currentPage, fetchSessions]);

  const handleContinueSession = (session) => {
    navigate(`/simulation/${session.id}`);
  };

  const applyFilters = useCallback((nextFilters) => {
    setFilterForm(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    updateUrlParams(nextFilters, 1);
    fetchSessions(1, nextFilters);
  }, [updateUrlParams, fetchSessions]);

  const handleFilterChange = (field, value) => {
    applyFilters({ ...filterForm, [field]: value });
  };

  const handleFilterDraftChange = (field, value) => {
    setFilterForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilterFieldCommit = (field, value) => {
    if (appliedFilters[field] === value) return;
    applyFilters({ ...filterForm, [field]: value });
  };

  const handleLanguageToggle = (language) => {
    const currentLanguages = filterForm.languages || [];
    const isSelected = currentLanguages.includes(language);
    const nextLanguages = isSelected
      ? currentLanguages.filter((l) => l !== language)
      : [...currentLanguages, language];
    applyFilters({ ...filterForm, languages: nextLanguages });
  };

  const handleClearFilters = () => {
    applyFilters(EMPTY_FILTERS);
  };

  const handlePageChange = (_event, page) => {
    setCurrentPage(page);
    updateUrlParams(appliedFilters, page);
    fetchSessions(page, appliedFilters);
  };

  return (
    <>
      {sessionsLoadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {sessionsLoadError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: filtersExpanded ? 2 : 1.25, '&:last-child': { pb: filtersExpanded ? 2 : 1.25 } }}>
          <Box
            onClick={() => setFiltersExpanded((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              px: filtersExpanded ? 0 : 0.5,
              py: filtersExpanded ? 0 : 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterList sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant={filtersExpanded ? 'h6' : 'subtitle1'}>Filters</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setFiltersExpanded((prev) => !prev);
              }}
              aria-label={filtersExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {filtersExpanded && (
            <>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4} md={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Session ID"
                    type="number"
                    value={filterForm.session_id}
                    onChange={(e) => handleFilterDraftChange('session_id', e.target.value)}
                    onBlur={(e) => handleFilterFieldCommit('session_id', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFilterFieldCommit('session_id', e.target.value);
                    }}
                    placeholder="Search by ID"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Patient</InputLabel>
                    <Select
                      value={filterForm.persona_id}
                      label="Patient"
                      onChange={(e) => handleFilterChange('persona_id', e.target.value)}
                    >
                      <MenuItem value="">All Patients</MenuItem>
                      {personas.map((persona) => (
                        <MenuItem key={persona.id} value={String(persona.id)}>
                          {persona.character_id
                            ? `${persona.name} (#${persona.character_id})`
                            : persona.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} md={1.25}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min Messages"
                    type="number"
                    value={filterForm.min_messages}
                    onChange={(e) => handleFilterDraftChange('min_messages', e.target.value)}
                    onBlur={(e) => handleFilterFieldCommit('min_messages', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFilterFieldCommit('min_messages', e.target.value);
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={1.25}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Max Messages"
                    type="number"
                    value={filterForm.max_messages}
                    onChange={(e) => handleFilterDraftChange('max_messages', e.target.value)}
                    onBlur={(e) => handleFilterFieldCommit('max_messages', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFilterFieldCommit('max_messages', e.target.value);
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={filterForm.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={filterForm.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Feedback Status</InputLabel>
                    <Select
                      value={filterForm.has_feedback}
                      label="Feedback Status"
                      onChange={(e) => handleFilterChange('has_feedback', e.target.value)}
                    >
                      <MenuItem value="">All Sessions</MenuItem>
                      <MenuItem value="true">With Feedback</MenuItem>
                      <MenuItem value="false">Without Feedback</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select
                      value={filterForm.mode}
                      label="Mode"
                      onChange={(e) => handleFilterChange('mode', e.target.value)}
                    >
                      <MenuItem value="">All Modes</MenuItem>
                      {SESSION_MODE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'text.secondary' }}>
                      Languages
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {SUPPORTED_LANGUAGES.map((language) => {
                        const isActive = filterForm.languages && filterForm.languages.includes(language);
                        return (
                          <Chip
                            key={language}
                            label={language}
                            onClick={() => handleLanguageToggle(language)}
                            color={isActive ? 'primary' : 'default'}
                            variant={isActive ? 'filled' : 'outlined'}
                            size="medium"
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: 2,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button variant="outlined" size="small" onClick={handleClearFilters}>
                  Clear
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Sessions ({totalSessions} total, showing {sessions.length} on page {currentPage})
            </Typography>
            {totalSessions > sessionsPerPage && (
              <Pagination
                count={Math.ceil(totalSessions / sessionsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sessions.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No sessions found matching the current filters.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 'calc(100vh - 300px)' }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      Session ID
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      Patient
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      Mode
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      Start Time
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      Last Updated
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      Messages
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow
                      key={session.id}
                      hover
                      onClick={() => handleContinueSession(session)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        '& .MuiTableCell-root': {
                          py: 0.65,
                          fontSize: '0.82rem',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{session.id}</Typography>
                          {session.is_resimulation && (
                            <Tooltip
                              title={
                                session.resimulation_source_session_id
                                  ? `Resimulation session (from Session #${session.resimulation_source_session_id})`
                                  : 'Resimulation session'
                              }
                              arrow
                            >
                              <ReplayIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                            </Tooltip>
                          )}
                          {session.is_test_plan_session && (
                            <Tooltip title="Automated test plan session" arrow>
                              <GridOnIcon sx={{ fontSize: 16, color: 'info.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SessionPatientIdLabel session={session} />
                          <Typography variant="body2" noWrap>
                            {getSessionPatientDisplayName(session)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SessionModeChip mode={session.mode} />
                      </TableCell>
                      <TableCell>{formatSessionDateTime(session.start_time)}</TableCell>
                      <TableCell>{formatSessionDateTime(session.updated_at || session.start_time)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Tooltip
                            title={`${session.patient_exam_message_count ?? 0} patient exam message${(session.patient_exam_message_count ?? 0) !== 1 ? 's' : ''}`}
                            arrow
                          >
                            <Badge
                              badgeContent={session.patient_exam_message_count ?? session.message_count}
                              color="primary"
                              sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                            >
                              <Message sx={{ fontSize: 17 }} />
                            </Badge>
                          </Tooltip>
                          {(session.surgery_message_count > 0) && (
                            <Tooltip
                              title={`${session.surgery_message_count} surgery message${session.surgery_message_count !== 1 ? 's' : ''}`}
                              arrow
                            >
                              <Badge
                                badgeContent={session.surgery_message_count}
                                color="error"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                              >
                                <SurgeryIcon sx={{ fontSize: 17 }} />
                              </Badge>
                            </Tooltip>
                          )}
                          {session.feedback_count > 0 && (
                            <Tooltip
                              title={`${session.feedback_count} feedback submission${session.feedback_count !== 1 ? 's' : ''}`}
                              arrow
                            >
                              <Badge
                                badgeContent={session.feedback_count}
                                color="secondary"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                              >
                                <Feedback sx={{ fontSize: 17 }} />
                              </Badge>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalSessions > sessionsPerPage && !loading && sessions.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Pagination
                  count={Math.ceil(totalSessions / sessionsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="body2" color="text.secondary">
                  Showing {((currentPage - 1) * sessionsPerPage) + 1}-{Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions} sessions
                </Typography>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ClientSessionHistoryView;
